<template>
  <nav
    id="app-sidenav"
    :class="[
      'side-navigation flex flex-col bg-white text-gray-800 shadow-[4px_0_24px_rgba(0,0,0,0.04)]',
      isMobile ? 'side-navigation-mobile' : 'side-navigation-desktop',
      isMobile && !mobileMenuOpen ? 'side-navigation-mobile-closed' : '',
      isMobile && mobileMenuOpen ? 'side-navigation-mobile-open' : '',
      isCollapsed && !isMobile ? 'collapsed' : '',
    ]"
  >
    <!-- Mobile: drawer header - brand + close -->
    <div
      v-if="isMobile"
      class="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-gray-200 px-3"
    >
      <router-link
        to="/overview"
        class="flex min-w-0 flex-1 items-center"
        @click="onBrandNavigate"
      >
        <img
          :src="dmsLogo"
          alt="DM Suite"
          class="h-8 w-auto max-w-[70%] object-contain object-left"
        >
      </router-link>
      <button
        type="button"
        class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
        aria-label="Close menu"
        @click="onToggleClick"
      >
        <i
          class="fas fa-times text-lg"
          aria-hidden="true"
        />
      </button>
    </div>

    <!-- Desktop: header -->
    <div
      v-if="!isMobile"
      :class="[
        'flex shrink-0 items-center bg-white py-4 transition-all duration-200',
        isCollapsed ? 'brand-row-collapsed justify-center border-b border-gray-200 px-2' : 'brand-row-expanded justify-between gap-2 px-6',
      ]"
    >
      <div
        class="brand-link-wrapper flex min-w-0 flex-1 items-center py-1 overflow-hidden"
        :class="{ hidden: isCollapsed }"
      >
        <router-link
          to="/overview"
          class="flex min-w-0 flex-1 items-center"
          @click="onBrandNavigate"
        >
          <img
            :src="dmsLogo"
            alt="DM Suite"
            class="h-8 w-auto max-w-full object-contain object-left"
          >
        </router-link>
      </div>

      <button
        v-tooltip="{
          content: isCollapsed ? 'Open sidebar' : 'Close sidebar',
          placement: 'right',
          hideOnTargetClick: true,
        }"
        type="button"
        :class="[
          'transition-colors text-gray-600 hover:text-gray-900 hover:bg-gray-100 flex items-center justify-center rounded-md',
          isCollapsed ? 'group relative h-10 w-10 text-gray-700' : 'h-9 w-9 shrink-0',
        ]"
        :aria-label="isCollapsed ? 'Open sidebar' : 'Close sidebar'"
        @click="onToggleClick"
      >
        <span
          v-if="isCollapsed"
          class="relative flex h-full w-full items-center justify-center"
        >
          <img
            :src="sidebarLogo"
            alt=""
            class="absolute inset-0 m-auto h-8 w-8 object-contain transition-opacity duration-150 group-hover:opacity-0"
          >
          <span
            class="pointer-events-none flex h-full w-full items-center justify-center opacity-0 transition-opacity duration-150 group-hover:opacity-100"
            aria-hidden="true"
          >
            <svg
              class="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="4"
                y="5"
                width="16"
                height="14"
                rx="2"
                stroke="currentColor"
                stroke-width="1.5"
              />
              <line
                x1="9.25"
                y1="5"
                x2="9.25"
                y2="19"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </span>
        </span>
        <svg
          v-else
          class="h-5 w-5"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <rect
            x="4"
            y="5"
            width="16"
            height="14"
            rx="2"
            stroke="currentColor"
            stroke-width="1.5"
          />
          <line
            x1="9.25"
            y1="5"
            x2="9.25"
            y2="19"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </div>

    <!-- Navigation Items Container -->
    <div class="nav-container relative flex min-h-0 flex-1 flex-col">
      <!-- Skeleton Loading State -->
      <div
        v-if="isSidenavLoading && (!orderedSections || orderedSections.length === 0)"
        class="p-4 space-y-4 animate-pulse"
      >
        <div class="h-3 w-16 bg-gray-200 rounded px-4" />
        <div class="h-8 bg-gray-100 rounded-md" />
        <div class="h-8 bg-gray-100 rounded-md" />
        <div class="h-3 w-20 bg-gray-200 rounded px-4" />
        <div class="h-8 bg-gray-100 rounded-md" />
        <div class="h-8 bg-gray-100 rounded-md" />
      </div>

      <!-- Scrollable Navigation Items -->
      <div
        v-else
        class="side-nav nav-scroll-container min-h-0 flex-1 overflow-y-auto"
      >
        <ul class="scrollbar-hidden min-h-0 flex-1 overflow-y-auto">
          <!-- Iterate through sections in order -->
          <li
            v-for="section in orderedSections"
            :key="section.id"
            class="list-none"
          >
            <!-- Section Title -->
            <h2
              v-if="section.label && section.label.trim() !== ''"
              class="nav-section-title px-8 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
            >
              {{ section.label }}
            </h2>

            <!-- Navigation Links for this section -->
            <ul class="list-none p-0">
              <li
                v-for="item in section.displayItems"
                :key="item.id"
                class="list-none relative group/navgroup"
              >
                <div
                  :class="[
                    'nav-item group relative flex cursor-pointer items-center transition-colors hover:bg-blue-400',
                    isCollapsed && !isMobile ? 'justify-center px-2 py-2.5' : 'gap-3 px-8 py-2.5',
                    { 'bg-blue-100 font-semibold': isActive(item) },
                  ]"
                  @click="handleNavItemClick(item)"
                >
                  <span
                    class="nav-item-icon-wrap inline-flex flex-shrink-0"
                  >
                    <AppIcon
                      :app="item"
                      :size="24"
                    />
                  </span>
                  <span class="nav-item-label min-w-0 flex-1 truncate text-[0.9375rem] leading-snug text-gray-800">
                    {{ item.label }}
                  </span>
                  <span
                    v-if="getNotificationBadgeCount(item) > 0"
                    :aria-label="`${getNotificationBadgeCount(item)} unread notifications`"
                    class="nav-badge ml-auto inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-[#d92d20] px-1.5 text-[10px] font-bold text-white"
                  >
                    {{ badgeCountText(getNotificationBadgeCount(item)) }}
                  </span>
                  <i
                    v-if="hasNavChildren(item)"
                    class="nav-chevron fas fa-chevron-down text-xs text-gray-500"
                    aria-hidden="true"
                  />
                  <span
                    v-if="getChatBadgeCount(item) > 0"
                    class="nav-chat-badge pointer-events-none absolute right-3 top-1/2 inline-flex h-5 min-w-5 -translate-y-1/2 items-center justify-center rounded-full px-1.5 text-[10px] font-bold shadow-sm ring-2 ring-white/90"
                    :class="chatBadgeClass()"
                  >
                    {{ badgeCountText(getChatBadgeCount(item)) }}
                  </span>
                </div>

                <!-- Collapsed Hover Submenu / Flyout (Items with children) -->
                <div
                  v-if="hasNavChildren(item)"
                  class="nav-collapsed-flyout pointer-events-none invisible absolute left-full top-0 z-50 pl-2 -translate-x-1 opacity-0 transition-all duration-150 group-hover/navgroup:pointer-events-auto group-hover/navgroup:visible group-hover/navgroup:translate-x-0 group-hover/navgroup:opacity-100 before:absolute before:-left-3 before:top-0 before:bottom-0 before:w-3 before:content-['']"
                >
                  <div class="w-56 rounded-xl border border-gray-200 bg-white p-1.5 shadow-2xl backdrop-blur-sm">
                    <div class="border-b border-gray-100 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                      {{ item.label }}
                    </div>
                    <ul class="list-none p-0 py-1 space-y-0.5">
                      <li
                        v-for="child in item.children"
                        :key="child.id"
                        :class="[
                          'flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-gray-700 transition hover:bg-blue-500 hover:text-white',
                          { 'bg-blue-50 font-semibold text-blue-600': isActive(child) },
                        ]"
                        @click.stop="navigateTo(child)"
                      >
                        <span class="inline-flex h-5 w-5 shrink-0 items-center justify-center">
                          <AppIcon
                            :app="child"
                            :size="16"
                          />
                        </span>
                        <span class="truncate">{{ child.label }}</span>
                        <span
                          v-if="getNotificationBadgeCount(child) > 0"
                          class="ml-auto inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[#d92d20] px-1 text-[9px] font-bold text-white"
                        >
                          {{ badgeCountText(getNotificationBadgeCount(child)) }}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>

                <!-- Collapsed Hover Tooltip (Items without children) -->
                <div
                  v-else
                  class="nav-collapsed-tooltip pointer-events-none invisible absolute left-full top-1/2 z-50 pl-2 -translate-y-1/2 -translate-x-1 opacity-0 transition-all duration-150 group-hover/navgroup:pointer-events-auto group-hover/navgroup:visible group-hover/navgroup:translate-x-0 group-hover/navgroup:opacity-100"
                >
                  <div class="whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1 text-xs font-medium text-white shadow-xl">
                    {{ item.label }}
                  </div>
                </div>

                <!-- Expanded accordion child list -->
                <ul
                  v-if="hasNavChildren(item) && isNavGroupOpen(item)"
                  class="nav-child-list list-none bg-gray-50/70 py-1"
                >
                  <li
                    v-for="child in item.children"
                    :key="child.id"
                    :class="[
                      'nav-item group relative flex cursor-pointer items-center transition-colors hover:bg-blue-400',
                      isCollapsed && !isMobile ? 'justify-center px-2 py-2.5' : 'gap-3 py-2.5 pl-12 pr-6',
                      { 'bg-blue-100 font-semibold': isActive(child) },
                    ]"
                    @click="navigateTo(child)"
                  >
                    <span class="nav-item-icon-wrap inline-flex flex-shrink-0">
                      <AppIcon
                        :app="child"
                        :size="20"
                      />
                    </span>
                    <span class="nav-item-label truncate text-sm leading-snug text-gray-800">
                      {{ child.label }}
                    </span>
                    <span
                      v-if="getNotificationBadgeCount(child) > 0"
                      :aria-label="`${getNotificationBadgeCount(child)} unread notifications`"
                      class="nav-badge ml-auto inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-[#d92d20] px-1.5 text-[10px] font-bold text-white"
                    >
                      {{ badgeCountText(getNotificationBadgeCount(child)) }}
                    </span>
                  </li>
                </ul>
              </li>
            </ul>

            <!-- Divider -->
            <hr class="nav-divider my-4">
          </li>
          <!-- <template v-if="orderedSections.length === 0">
            <li class="px-8 py-6 text-sm text-gray-500 text-center">
              No navigation items available for this account.
            </li>
          </template> -->
        </ul>
      </div>

      <!-- Sticky Bottom Section -->
      <div
        v-if="userType === 'user' && isSuperAdmin"
        class="sticky-bottom-section shrink-0 border-t border-gray-200 bg-white"
      >
        <div class="relative group/navgroup">
          <div
            :class="[
              'nav-item group relative flex cursor-pointer items-center transition-colors hover:bg-blue-400',
              isCollapsed && !isMobile ? 'justify-center px-2 py-3' : 'space-x-4 px-8 py-3',
            ]"
            @click="navigateTo('/new-application')"
          >
            <span
              class="nav-item-icon-wrap inline-flex flex-shrink-0"
            >
              <i class="fas fa-plus text-xl" />
            </span>
            <span class="nav-item-label truncate text-base">New Application</span>
          </div>
          <div
            class="nav-collapsed-tooltip pointer-events-none invisible absolute left-full top-1/2 z-50 pl-2 -translate-y-1/2 -translate-x-1 opacity-0 transition-all duration-150 group-hover/navgroup:pointer-events-auto group-hover/navgroup:visible group-hover/navgroup:translate-x-0 group-hover/navgroup:opacity-100"
          >
            <div class="whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1 text-xs font-medium text-white shadow-xl">
              New Application
            </div>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { useRenewalNotifications } from "@/composables/useRenewalNotifications";
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import http from "@/services/http";
import { eventBus } from "@/eventBus";
import { useAuthStore } from "@/store/auth";
import { storeToRefs } from "pinia";
import dmsLogo from "@/assets/dms-logo.png";
import { subscribeToChatConversation } from "@/modules/chat/services/chatRealtime";
import { useSideNavigationQuery } from "@/composables/useSideNavigationQuery";
import { normalizeSidebarRoutePath } from "@/composables/useSideNavigationQuery";
import { useAccessControl } from "@/composables/useAccessControl";
import {
  ACCESS_MODULE_KEYS,
  ACCESS_PERMISSION_KEYS,
} from "@/constants/accessControl";
import { canAccessRoute } from "@/utils/authRedirect";
import notificationSound from "@/assets/sounds/message-notification.wav";
import {
  isSidenavImageIcon,
  resolveSidenavIconUrl,
} from "@/utils/sidenavIcons";
import AppIcon from "@/components/common/AppIcon.vue";
import {
  buildNavigationTree,
  isSettingsNavItem,
  hasRouteInItems,
} from "@/utils/navigationTree";

const baseUrl = process.env.BASE_URL || "/";
const publicAssetUrl = (path) => `${baseUrl.replace(/\/?$/, "/")}${path}`;
const sidebarLogo = publicAssetUrl("assets/logo.svg");
const inboxIcon = publicAssetUrl(
  "assets/icons/side-navigation-icons/inbox-icon.svg"
);
const LEADS_ROUTE_PATH = "/leads";
const ONBOARDING_ROUTE_PATH = "/onboarding";
const EMAIL_TEMPLATE_CHILDREN = [
  {
    id: "email-template-balance-collection",
    label: "Balance Collection",
    route: "/email-templates/balance-collection",
    icon: "fas fa-wallet",
  },
  {
    id: "email-template-renewal",
    label: "Renewal",
    route: "/email-templates/renewal",
    icon: "fas fa-calendar-rotate-left",
  },
  {
    id: "email-template-offboarding-email",
    label: "Offboarding Email",
    route: "/email-templates/offboarding-email",
    icon: "fas fa-person-walking-arrow-right",
  },
  {
    id: "email-template-end-of-contract",
    label: "End of Contract",
    route: "/email-templates/end-of-contract",
    icon: "fas fa-file-signature",
  },
];

export default {
  name: "SideNavigation",
  components: {
    AppIcon,
  },
  props: {
    isCollapsed: {
      type: Boolean,
      default: false,
    },
    mobileMenuOpen: {
      type: Boolean,
      default: false,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["closeMobileMenu", "update:isCollapsed"],
  setup(props, { emit }) {
    const { unreadCount, notificationsLoading, notificationsError } = useRenewalNotifications();
    const getNotificationBadgeCount = (item) =>
      normalizeRoutePath(item?.route) === "settings/notifications" && !notificationsLoading.value && !notificationsError.value
        ? unreadCount.value : 0;
    const onToggleClick = () => {
      if (props.isMobile) {
        emit("closeMobileMenu");
      } else {
        emit("update:isCollapsed", !props.isCollapsed);
      }
    };

    const onBrandNavigate = () => {
      if (props.isMobile) emit("closeMobileMenu");
    };

    const router = useRouter();
    const authStore = useAuthStore();
    const { userType, clientSidenavUserId, isSuperAdmin } =
      storeToRefs(authStore);
    const { canPermission, canModule } = useAccessControl();
    const canAccessInbox = computed(() =>
      canAccessRoute("/inbox", authStore)
    );
    const canCreateClientApplications = computed(() =>
      canPermission(ACCESS_PERMISSION_KEYS.CLIENTS_CREATE)
    );
    const canManageSystemApps = computed(() =>
      canPermission(ACCESS_PERMISSION_KEYS.USERS_MANAGE_PERMISSIONS)
    );
    const canManageNotifications = computed(() =>
      canPermission(ACCESS_PERMISSION_KEYS.USERS_MANAGE_PERMISSIONS)
    );
    const canAccessReportsV2 = computed(
      () => userType.value !== "client" || canModule("reports_v2")
    );
    const sidenavQuery = useSideNavigationQuery(authStore);
    const isSidenavLoading = computed(
      () => sidenavQuery.isLoading.value || !authStore.hydrated
    );
    const sidenavItems = computed(() => sidenavQuery.data.value?.items || []);
    const sidenavSections = computed(
      () => sidenavQuery.data.value?.sections || []
    );
    const hasExplicitSidenavAssignments = computed(
      () => !!sidenavQuery.data.value?.hasExplicitSidenavAssignments
    );
    const chatUnreadCount = ref(0);
    const chatUnreadSubscriptions = new Map();
    const expandedNavGroups = ref([]);

    const playNotification = () => {
      const audio = new Audio(notificationSound);
      audio.play().catch((err) => {
        console.warn("Audio playback blocked or failed:", err);
      });
    };

    const isChatRoute = computed(() => {
      return (
        router.currentRoute.value.path === "/communication/chat" ||
        router.currentRoute.value.name === "ChatTool"
      );
    });

    const isImageIcon = isSidenavImageIcon;
    const resolveIconUrl = resolveSidenavIconUrl;

    const normalizeRoutePath = (route) =>
      String(route || "")
        .trim()
        .toLowerCase()
        .replace(/^\/+/, "");

    const isInboxRoutePath = (route) => {
      const normalizedRoute = normalizeRoutePath(route);
      return normalizedRoute === "inbox";
    };

    const isChatNavItem = (item) =>
      normalizeRoutePath(item?.route) === "communication/chat";

    const isLeadNavItem = (item) => normalizeRoutePath(item?.route) === "leads";

    const isRenewalTrackingNavItem = (item) =>
      normalizeRoutePath(item?.route) === "renewal-tracking";

    const isInboxNavItem = (item) => {
      const normalizedLabel = String(item?.label || "")
        .trim()
        .toLowerCase();

      return isInboxRoutePath(item?.route) || normalizedLabel === "inbox";
    };

    const shouldShowInboxNavItem = () => canAccessInbox.value;
    const isOverviewNavItem = (item) =>
      normalizeRoutePath(item?.route) === "overview";
    const isEmailTemplateRoute = (route) =>
      normalizeRoutePath(route).startsWith("email-templates/");

    const isOnboardingNavItem = (item) => {
      const route = String(item?.route || "").replace(/^\/+/, "");
      return (
        route === "onboarding" ||
        route === "client/initial-plan" ||
        route === "apps/onboarding" ||
        route === "client/onboarding"
      );
    };

    const isMicroappOnboardingNavItem = (item) =>
      normalizeRoutePath(item?.route) === "onboarding/microapp";

    const badgeCountText = (count) => {
      if (!count || count < 1) return "";
      return count > 99 ? "99+" : `${count}`;
    };

    const chatBadgeClass = () => {
      if (authStore.accountType === "client") {
        return "bg-green-600 text-white";
      }

      if (isSuperAdmin.value) {
        return "bg-purple-600 text-white";
      }

      return "bg-blue-600 text-white";
    };

    const clearChatUnreadSubscriptions = () => {
      chatUnreadSubscriptions.forEach((subscription) => {
        subscription.leave();
      });
      chatUnreadSubscriptions.clear();
    };

    const isUnreadIncomingMessage = (message) => {
      if (authStore.accountType === "client") {
        return message?.sender_type === "user";
      }

      return message?.sender_type === "client";
    };

    const refreshChatUnreadBadge = async () => {
      clearChatUnreadSubscriptions();

      if (!authStore.isAuthenticated || isChatRoute.value) {
        chatUnreadCount.value = 0;
        return;
      }

      try {
        const { data } = await http.get("/api/chat/conversations");
        const conversations = Array.isArray(data?.data) ? data.data : [];

        if (isChatRoute.value || !authStore.isAuthenticated) {
          return;
        }

        chatUnreadCount.value = conversations.reduce((total, conversation) => {
          return total + Number(conversation?.unread_message_count || 0);
        }, 0);

        await Promise.all(
          conversations.map(async (conversation) => {
            const conversationId = Number(conversation?.id || 0);

            if (conversationId <= 0 || isChatRoute.value) {
              return;
            }

            const subscription = await subscribeToChatConversation(
              conversationId,
              {
                onMessage: (message) => {
                  if (isChatRoute.value || !isUnreadIncomingMessage(message)) {
                    return;
                  }

                  chatUnreadCount.value += 1;
                  playNotification();
                },
                onTypingStarted: () => undefined,
                onTypingStopped: () => undefined,
                onError: (error) => {
                  console.error(
                    "Sidebar chat unread subscription failed",
                    error
                  );
                },
              }
            );

            if (!subscription || isChatRoute.value) {
              subscription?.leave();
              return;
            }

            chatUnreadSubscriptions.set(conversationId, subscription);
          })
        );
      } catch (error) {
        const status = Number(error?.response?.status || 0);
        if (![400, 403].includes(status)) {
          console.error("Failed to load chat unread count:", error);
        }
        chatUnreadCount.value = 0;
      }
    };

    const getChatBadgeCount = (item) => {
      if (!isChatNavItem(item) || isChatRoute.value) {
        return 0;
      }

      return chatUnreadCount.value;
    };

    watch([userType, clientSidenavUserId], async () => {
      await refreshChatUnreadBadge();
    });

    watch(isChatRoute, async () => {
      if (isChatRoute.value) {
        clearChatUnreadSubscriptions();
        chatUnreadCount.value = 0;
        return;
      }

      await refreshChatUnreadBadge();
    });

    const toggleCollapse = () => {
      emit("update:isCollapsed", !props.isCollapsed);
    };

    const navigateTo = (itemOrPath) => {
      if (!itemOrPath) return;

      if (typeof itemOrPath === "string") {
        router.push(itemOrPath);
        if (props.isMobile) emit("closeMobileMenu");
        return;
      }

      const item = itemOrPath;
      if (item.iframe_url && item.route) {
        const path = normalizeSidebarRoutePath(item);
        if (!path) return;
        router.push(path);
        if (props.isMobile) emit("closeMobileMenu");
        return;
      }

      if (typeof item.route === "string") {
        const path = item.route.startsWith("/") ? item.route : `/${item.route}`;
        router.push(path);
        if (props.isMobile) emit("closeMobileMenu");
      }
    };

    const hasNavChildren = (item) =>
      Array.isArray(item?.children) && item.children.length > 0;

    const isNavGroupOpen = (item) =>
      expandedNavGroups.value.includes(String(item?.id));

    const toggleNavGroup = (item) => {
      const groupId = String(item?.id);
      const isOpen = expandedNavGroups.value.includes(groupId);
      expandedNavGroups.value = isOpen
        ? expandedNavGroups.value.filter((id) => id !== groupId)
        : [...expandedNavGroups.value, groupId];
    };

    const handleNavItemClick = (item) => {
      if (!hasNavChildren(item)) {
        navigateTo(item);
        return;
      }

      if (props.isCollapsed && !props.isMobile) {
        emit("update:isCollapsed", false);
        if (!isNavGroupOpen(item)) toggleNavGroup(item);
        return;
      }

      toggleNavGroup(item);
    };

    const isActive = (item) => {
      if (hasNavChildren(item)) {
        return item.children.some((child) => isActive(child));
      }

      const current = router.currentRoute.value.path;
      if (item.iframe_url && item.route) {
        const path = normalizeSidebarRoutePath(item);
        return current === path;
      }
      if (typeof item.route === "string") {
        const path = item.route.startsWith("/") ? item.route : `/${item.route}`;
        const [pathname] = path.split("?");
        if (path === "/onboarding") {
          return (
            current === "/onboarding" ||
            (current.startsWith("/onboarding/") &&
              current !== "/onboarding/microapp")
          );
        }
        if (isInboxRoutePath(path)) {
          return isInboxRoutePath(current);
        }
        return current === pathname;
      }
      return false;
    };

    const isUsersManagementItem = (item) => {
      const normalizedRoute = String(item?.route || "")
        .trim()
        .toLowerCase()
        .replace(/^\/+/, "");
      const normalizedLabel = String(item?.label || "")
        .trim()
        .toLowerCase();

      return normalizedRoute === "users" || normalizedLabel === "users";
    };

    const buildClientManagementItem = (item) => ({
      ...item,
      id: `clients-${item.id}`,
      label: "Account Profiles",
      route: "/clients",
    });

    const buildRenewalManagementItem = (item) => ({
      ...item,
      id: `renewals-${item.id}`,
      label: "Renewal",
      route: "/renewals",
      icon: "fas fa-calendar-alt",
    });

    const buildPackageServicesManagementItem = (item) => ({
      ...item,
      id: `package-services-${item.id}`,
      label: "Packages & Services",
      route: "/admin/package-services",
    });

    const buildSystemAppsManagementItem = (item) => ({
      ...item,
      id: `system-apps-${item.id}`,
      label: "System Apps Management",
      route: "/settings/system-apps",
      icon: "fas fa-table-cells-large",
    });

    const buildSystemSettingsItem = (item) => ({
      ...item,
      id: `system-settings-${item.id}`,
      label: "System Settings",
      route: "/settings/system",
      icon: "fas fa-gear",
    });

    const buildEmailRecipientsItem = (item) => ({
      ...item,
      id: `email-recipients-${item.id}`,
      label: "Email Recipients",
      route: "/settings/email-recipients",
      icon: "fas fa-envelope-circle-check",
    });

    const buildNotificationManagementItem = (item) => ({
      ...item,
      id: `notifications-${item.id}`,
      label: "Notifications",
      route: "/settings/notifications",
      icon: "fas fa-bell",
    });

    const buildLeadsManagementItem = (section) => ({
      id: `leads-${section?.id ?? "synthetic"}`,
      label: "Lead Management",
      icon: "fas fa-user-plus",
      route: LEADS_ROUTE_PATH,
      iframe_url: null,
      category: section?.name || "communication",
      section_id: section?.id ?? null,
      order: 9999,
      is_active: true,
    });

    const buildInboxManagementItem = (section) => ({
      id: `inbox-${section?.id ?? "synthetic"}`,
      label: "Inbox",
      icon: inboxIcon,
      route: "/inbox",
      iframe_url: null,
      category: section?.name || "dashboard",
      section_id: section?.id ?? null,
      order: 9998,
      is_active: true,
    });

    const buildOnboardingManagementItem = (section) => ({
      id: `onboarding-${section?.id ?? "synthetic"}`,
      label: "Onboarding",
      icon: "fas fa-clipboard-list",
      route: authStore.accountType === "client" && authStore.clientProfile?.first_entry?.has_plan
        ? "/client/initial-plan" : ONBOARDING_ROUTE_PATH,
      iframe_url: null,
      category: section?.name || "apps",
      section_id: section?.id ?? null,
      order: 9998,
      is_active: true,
    });

    const buildEmailTemplatesItem = (section) => ({
      id: `email-templates-${section?.id ?? "synthetic"}`,
      label: "Email Templates",
      icon: "fas fa-envelope-open-text",
      children: EMAIL_TEMPLATE_CHILDREN.map((child) => ({
        ...child,
        id: `${child.id}-${section?.id ?? "synthetic"}`,
        iframe_url: null,
        category: section?.name || "dashboard",
        section_id: section?.id ?? null,
        order: 9997,
        is_active: true,
      })),
      is_active: true,
    });

    const buildReportsV2Item = (section) => ({
      id: "reports-v2-navigation-group",
      label: "Reports v2",
      icon: "fas fa-chart-line",
      is_active: true,
      children: [
        {
          id: "reports-v2-dashboard-item",
          label: "Reports Dashboard",
          route: "/reports-v2",
          icon: "fas fa-chart-pie",
          is_active: true,
        },
        {
          id: "reports-v2-seo-item",
          label: "SEO Reports",
          route: "/reports-v2/seo-reports",
          icon: "fas fa-search-dollar",
          is_active: true,
        },
      ],
    });

    const shouldInjectInboxItemIntoSection = (section) => {
      if (!shouldShowInboxNavItem()) {
        return false;
      }

      const sections = sidenavSections.value || [];
      const hasInboxAlready = sections.some((candidate) =>
        (candidate.items || []).some(
          (item) => item.is_active && isInboxNavItem(item)
        )
      );

      if (hasInboxAlready) {
        return false;
      }

      const sectionLabel = String(section?.label || section?.name || "")
        .trim()
        .toLowerCase();
      const hasDashboardSection = sections.some((candidate) => {
        const candidateLabel = String(candidate?.label || candidate?.name || "")
          .trim()
          .toLowerCase();

        return candidateLabel === "dashboard";
      });

      if (hasDashboardSection && sectionLabel !== "dashboard") {
        return false;
      }

      if (!hasDashboardSection && sections[0]?.id !== section?.id) {
        return false;
      }

      const sectionItems = Array.isArray(section?.items) ? section.items : [];
      return !sectionItems.some(
        (item) => item.is_active && isInboxNavItem(item)
      );
    };

    const shouldInjectEmailTemplatesItemIntoSection = (section) => {
      if (!canAccessEmailTemplates()) {
        return false;
      }

      const sections = sidenavSections.value || [];
      const hasEmailTemplatesAlready = sections.some((candidate) =>
        (candidate.items || []).some((item) => {
          if (!item?.is_active) {
            return false;
          }

          if (
            String(item?.label || "").trim().toLowerCase() === "email templates"
          ) {
            return true;
          }

          if (Array.isArray(item?.children) && item.children.length > 0) {
            return item.children.some((child) => isEmailTemplateRoute(child?.route));
          }

          return isEmailTemplateRoute(item?.route);
        })
      );

      if (hasEmailTemplatesAlready) {
        return false;
      }

      const sectionLabel = String(section?.label || section?.name || "")
        .trim()
        .toLowerCase();
      const hasDashboardSection = sections.some((candidate) => {
        const candidateLabel = String(candidate?.label || candidate?.name || "")
          .trim()
          .toLowerCase();

        return candidateLabel === "dashboard";
      });

      if (hasDashboardSection && sectionLabel !== "dashboard") {
        return false;
      }

      return !hasDashboardSection ? sections[0]?.id === section?.id : true;
    };

    const shouldShowLeadsNavItem = () => {
      return canAccessRoute(LEADS_ROUTE_PATH, authStore);
    };

    const shouldShowOnboardingNavItem = () => {
      if (authStore.accountType === "client") {
        return !!authStore.clientProfile?.first_entry?.has_plan;
      }
      if (authStore.accountType !== "internal_user") {
        return false;
      }

      return (
        userType.value === "user" &&
        !authStore.isTaskOnlyInternalUser &&
        canAccessRoute(ONBOARDING_ROUTE_PATH, authStore)
      );
    };

    const shouldShowMicroappOnboardingNavItem = () => {
      if (authStore.accountType !== "internal_user") {
        return false;
      }

      return (
        userType.value === "user" &&
        !authStore.isTaskOnlyInternalUser &&
        canAccessRoute("/clients", authStore)
      );
    };

    const canAccessRenewals = () => {
      if (authStore.accountType !== "internal_user") {
        return false;
      }

      return (
        userType.value === "user" &&
        canModule(ACCESS_MODULE_KEYS.RENEWALS) &&
        canPermission(ACCESS_PERMISSION_KEYS.RENEWALS_VIEW) &&
        canAccessRoute("/renewals", authStore)
      );
    };

    const canAccessEmailTemplates = () => {
      if (authStore.accountType !== "internal_user") {
        return false;
      }

      return (
        userType.value === "user" &&
        canAccessRoute("/email-templates/balance-collection", authStore)
      );
    };

    const shouldInjectLeadItemIntoSection = (section) => {
      if (hasExplicitSidenavAssignments.value) return false;
      if (!shouldShowLeadsNavItem()) return false;

      const sections = sidenavSections.value || [];
      const sectionItems = Array.isArray(section?.items) ? section.items : [];
      const hasLeadAlready = sections.some((candidate) =>
        (candidate.items || []).some(
          (item) => item.is_active && isLeadNavItem(item)
        )
      );

      if (hasLeadAlready) return false;

      const hasChatSomewhere = sections.some((candidate) =>
        (candidate.items || []).some(
          (item) => item.is_active && isChatNavItem(item)
        )
      );

      if (sectionItems.some((item) => item.is_active && isChatNavItem(item))) {
        return true;
      }

      return !hasChatSomewhere && sections[0]?.id === section?.id;
    };

    const shouldInjectOnboardingItemIntoSection = (section) => {
      if (!shouldShowOnboardingNavItem()) return false;

      const sections = sidenavSections.value || [];
      const sectionItems = Array.isArray(section?.items) ? section.items : [];
      const hasOnboardingAlready = sections.some((candidate) =>
        (candidate.items || []).some(
          (item) => item.is_active && isOnboardingNavItem(item)
        )
      );

      if (hasOnboardingAlready) return false;

      const hasOverviewSomewhere = sections.some((candidate) =>
        (candidate.items || []).some(
          (item) => item.is_active && isOverviewNavItem(item)
        )
      );

      if (
        sectionItems.some((item) => item.is_active && isOverviewNavItem(item))
      ) {
        return true;
      }

      return !hasOverviewSomewhere && sections[0]?.id === section?.id;
    };

    const groupSettingsItems = (items) => {
      items = items.filter((item) => normalizeRoutePath(item?.route) !== "settings/holidays");
      const systemSettingsIndex = items.findIndex((item) => normalizeRoutePath(item?.route) === "settings/system");
      if (isSuperAdmin.value && systemSettingsIndex >= 0) {
        items.splice(systemSettingsIndex + 1, 0, {
          id: "manage-holidays",
          label: "Manage Holidays",
          route: "/settings/holidays",
          icon: "fas fa-calendar-alt",
          is_active: true,
        });
      }
      const settingsRoutes = new Set([
        "settings/system-apps",
        "settings/system",
        "settings/email-recipients",
        "settings/holidays",
      ]);

      // Check if an authoritative or existing Settings parent item is already in items
      const existingSettingsIndex = items.findIndex((item) => {
        const label = String(item?.label || "").trim().toLowerCase();
        const route = normalizeRoutePath(item?.route);
        return (
          item?.id === "settings-navigation-group" ||
          label === "settings" ||
          route === "settings" ||
          (Array.isArray(item?.children) &&
            item.children.some((c) =>
              settingsRoutes.has(normalizeRoutePath(c?.route))
            ))
        );
      });

      const looseSettingsChildren = items.filter((item) =>
        settingsRoutes.has(normalizeRoutePath(item?.route))
      );

      // If an authoritative Settings parent already exists in items:
      if (existingSettingsIndex >= 0) {
        const canonicalParent = items[existingSettingsIndex];
        const mergedChildren = Array.isArray(canonicalParent.children)
          ? [...canonicalParent.children]
          : [];
        const existingChildRoutes = new Set(
          mergedChildren.map((c) => normalizeRoutePath(c?.route))
        );

        // Merge any loose settings items into the existing parent's children
        for (const child of looseSettingsChildren) {
          const childRoute = normalizeRoutePath(child?.route);
          if (!existingChildRoutes.has(childRoute)) {
            mergedChildren.push(child);
            existingChildRoutes.add(childRoute);
          }
        }

        // Filter out loose settings items and any secondary duplicate Settings entries
        const filteredItems = items.filter((item, idx) => {
          if (looseSettingsChildren.includes(item)) return false;
          if (idx !== existingSettingsIndex) {
            const label = String(item?.label || "").trim().toLowerCase();
            const route = normalizeRoutePath(item?.route);
            if (
              item?.id === "settings-navigation-group" ||
              (label === "settings" &&
                route === normalizeRoutePath(canonicalParent?.route))
            ) {
              // Merge its children into canonical parent before dropping
              if (Array.isArray(item?.children)) {
                for (const c of item.children) {
                  const cRoute = normalizeRoutePath(c?.route);
                  if (!existingChildRoutes.has(cRoute)) {
                    mergedChildren.push(c);
                    existingChildRoutes.add(cRoute);
                  }
                }
              }
              return false;
            }
          }
          return true;
        });

        return filteredItems.map((item) => {
          if (item.id === canonicalParent.id) {
            return {
              ...item,
              children: mergedChildren,
            };
          }
          return item;
        });
      }

      // If NO existing Settings parent exists, and there are loose settings children:
      if (looseSettingsChildren.length === 0) return items;

      const firstSettingsIndex = items.findIndex((item) =>
        settingsRoutes.has(normalizeRoutePath(item?.route))
      );
      const filteredItems = items.filter(
        (item) => !settingsRoutes.has(normalizeRoutePath(item?.route))
      );

      filteredItems.splice(firstSettingsIndex, 0, {
        id: "settings-navigation-group",
        label: "Settings",
        icon: "fas fa-gear",
        children: looseSettingsChildren,
      });

      return filteredItems;
    };

    const groupOnboardingItems = (items) => {
      if (authStore.accountType === "client" && authStore.clientProfile?.first_entry?.has_plan) {
        return items.map((item) => isOnboardingNavItem(item)
          ? { ...item, label: "Onboarding", route: "/client/initial-plan", iframe_url: null, icon: "fas fa-file-lines" }
          : item);
      }
      const onboardingIndex = items.findIndex((item) =>
        isOnboardingNavItem(item)
      );

      if (onboardingIndex < 0) return items;

      const onboardingItem = items[onboardingIndex];
      let microappItem = items.find((item) =>
        isMicroappOnboardingNavItem(item)
      );
      if (
        !microappItem &&
        shouldShowMicroappOnboardingNavItem() &&
        canAccessRoute("/onboarding/microapp", authStore)
      ) {
        microappItem = {
          id: "microapp-onboarding",
          label: "Microapp",
          route: "/onboarding/microapp",
          icon: "/assets/icons/side-navigation-icons/new-app-icon.svg",
          is_active: true,
        };
      }
      const groupedItems = items.filter(
        (item) => !isOnboardingNavItem(item) && !isMicroappOnboardingNavItem(item)
      );
      const onboardingChildren = [
        {
          ...onboardingItem,
          id: `dmp-${onboardingItem.id}`,
          label: "DMP",
          route: ONBOARDING_ROUTE_PATH,
          iframe_url: null,
          icon: "fas fa-file-lines",
        },
      ];

      if (microappItem) {
        onboardingChildren.push({
          ...microappItem,
          id: `microapp-${microappItem.id}`,
          label: "Microapp",
          route: "/onboarding/microapp",
        });
      }

      groupedItems.splice(onboardingIndex, 0, {
        id: "onboarding-navigation-group",
        label: "Onboarding",
        icon: onboardingItem.icon || "fas fa-rocket",
        children: onboardingChildren,
      });

      return groupedItems;
    };

    const groupManagementItems = (items) => {
      const managementRoutes = new Set(["clients", "renewals"]);
      const managementChildren = items
        .filter((item) => managementRoutes.has(normalizeRoutePath(item?.route)))
        .flatMap((item) => {
          const normalizedRoute = normalizeRoutePath(item?.route);

          if (normalizedRoute === "clients") {
            const accountProfiles = {
              ...item,
              label: "Account Profiles",
              route: "/clients",
            };

            return [
              accountProfiles,
            ];
          }

          if (normalizedRoute === "renewals") {
            return {
              ...item,
              label: "DMP Accounts",
              route: "/renewals",
            };
          }

          return item;
        });

      if (shouldShowMicroappOnboardingNavItem()) {
        const accountProfilesIndex = managementChildren.findIndex(
          (item) => normalizeRoutePath(item?.route) === "clients"
        );

        if (accountProfilesIndex >= 0) {
          managementChildren.splice(accountProfilesIndex + 1, 0, {
            id: "microapp-accounts",
            label: "Microapp Accounts",
            route: "/clients/microapp",
            icon: "fas fa-robot",
            is_active: true,
          });
        }
      }

      if (managementChildren.length === 0) return items;

      const firstManagementIndex = items.findIndex((item) =>
        managementRoutes.has(normalizeRoutePath(item?.route))
      );
      const groupedItems = items.filter(
        (item) => !managementRoutes.has(normalizeRoutePath(item?.route))
      );

      groupedItems.splice(firstManagementIndex, 0, {
        id: "clients-navigation-group",
        label: "Clients",
        icon:
          managementChildren[0]?.icon ||
          "/assets/icons/side-navigation-icons/user-icon.svg",
        children: managementChildren,
      });

      return groupedItems;
    };

    const groupUsersAccessItems = (items) => {
      const usersIndex = items.findIndex((item) => isUsersManagementItem(item));

      if (usersIndex < 0 || !isSuperAdmin.value) {
        return items;
      }

      const usersItem = items[usersIndex];
      const usersChildren = [
        {
          ...usersItem,
          id: `${usersItem.id}-internal-sidenav`,
          label: "Internal Users",
          route: "/users",
        },
        {
          ...usersItem,
          id: `${usersItem.id}-client-sidenav`,
          label: "Clients Sidenav",
          route: "/admin/manage-client-sidenav",
        },
      ];

      return [
        ...items.slice(0, usersIndex),
        {
          ...usersItem,
          id: `${usersItem.id}-access-group`,
          children: usersChildren,
        },
        ...items.slice(usersIndex + 1),
      ];
    };

    const getSectionItems = (section) => {
      if (!section.items || section.items.length === 0) return [];

      const rawItems = section.items || [];
      const topLevelItems = buildNavigationTree(rawItems);

      const activeItems = topLevelItems.filter((item) => item.is_active);
      const visibleItems = activeItems.filter(
        (item) =>
          (!isInboxNavItem(item) || shouldShowInboxNavItem()) &&
          (!isLeadNavItem(item) || shouldShowLeadsNavItem()) &&
          (!normalizeRoutePath(item?.route).startsWith("renewals") ||
            canAccessRenewals())
      );
      const hasClientManagementItem = activeItems.some((item) => {
        const normalizedRoute = String(item?.route || "")
          .trim()
          .toLowerCase()
          .replace(/^\/+/, "");
        return normalizedRoute === "clients";
      });
      const hasRenewalManagementItem = activeItems.some((item) => {
        const normalizedRoute = String(item?.route || "")
          .trim()
          .toLowerCase()
          .replace(/^\/+/, "");
        return normalizedRoute === "renewals";
      });
      const hasPackageServicesManagementItem = activeItems.some((item) => {
        const normalizedRoute = String(item?.route || "")
          .trim()
          .toLowerCase()
          .replace(/^\/+/, "");
        return normalizedRoute === "admin/package-services";
      });

      const allSidenavItems = sidenavSections.value.flatMap(
        (s) => s.items || []
      );
      const hasSystemAppsManagementItem =
        hasRouteInItems(activeItems, "settings/system-apps") ||
        hasRouteInItems(allSidenavItems, "settings/system-apps");
      const hasSystemSettingsItem =
        hasRouteInItems(activeItems, "settings/system") ||
        hasRouteInItems(allSidenavItems, "settings/system");
      const hasEmailRecipientsItem =
        hasRouteInItems(activeItems, "settings/email-recipients") ||
        hasRouteInItems(allSidenavItems, "settings/email-recipients");
      const hasSettingsParentItem =
        activeItems.some((i) => isSettingsNavItem(i)) ||
        allSidenavItems.some((i) => isSettingsNavItem(i));

      const hasNotificationManagementItem = activeItems.some((item) => {
        const normalizedRoute = String(item?.route || "")
          .trim()
          .toLowerCase()
          .replace(/^\/+/, "");
        return normalizedRoute === "settings/notifications";
      });
      const hasEmailTemplatesItem = activeItems.some((item) => {
        if (
          String(item?.label || "").trim().toLowerCase() === "email templates"
        ) {
          return true;
        }

        if (Array.isArray(item?.children) && item.children.length > 0) {
          return item.children.some((child) => isEmailTemplateRoute(child?.route));
        }

        return isEmailTemplateRoute(item?.route);
      });
      const shouldInjectLeadItem = shouldInjectLeadItemIntoSection(section);
      const shouldInjectOnboardingItem =
        shouldInjectOnboardingItemIntoSection(section);
      const results = visibleItems.flatMap((item) => {
        const injectedItems = [];
        if (
          userType.value === "user" &&
          isUsersManagementItem(item) &&
          !hasClientManagementItem &&
          canAccessRoute("/clients", authStore)
        ) {
          injectedItems.push(buildClientManagementItem(item));
        }

        if (
          userType.value === "user" &&
          isUsersManagementItem(item) &&
          !hasRenewalManagementItem &&
          canAccessRenewals()
        ) {
          injectedItems.push(buildRenewalManagementItem(item));
        }

        if (
          userType.value === "user" &&
          isUsersManagementItem(item) &&
          !hasPackageServicesManagementItem &&
          canAccessRoute("/admin/package-services", authStore)
        ) {
          injectedItems.push(buildPackageServicesManagementItem(item));
        }

        if (
          userType.value === "user" &&
          isUsersManagementItem(item) &&
          !hasSystemAppsManagementItem &&
          !hasSettingsParentItem &&
          canManageSystemApps.value
        ) {
          injectedItems.push(buildSystemAppsManagementItem(item));
        }

        if (
          userType.value === "user" &&
          canManageSystemApps.value &&
          isUsersManagementItem(item) &&
          !hasSystemSettingsItem &&
          !hasSettingsParentItem
        ) {
          injectedItems.push(buildSystemSettingsItem(item));
        }

        if (
          userType.value === "user" &&
          canManageSystemApps.value &&
          isUsersManagementItem(item) &&
          !hasEmailRecipientsItem &&
          !hasSettingsParentItem
        ) {
          injectedItems.push(buildEmailRecipientsItem(item));
        }

        if (
          userType.value === "user" &&
          canManageSystemApps.value &&
          isSettingsNavItem(item)
        ) {
          // If this is the canonical Settings parent, ensure required management items are present as children
          const existingChildRoutes = new Set(
            (item.children || []).map((c) => normalizeRoutePath(c?.route))
          );
          const additionalChildren = [];
          if (!existingChildRoutes.has("settings/system-apps")) {
            additionalChildren.push(buildSystemAppsManagementItem(item));
          }
          if (!existingChildRoutes.has("settings/system")) {
            additionalChildren.push(buildSystemSettingsItem(item));
          }
          if (!existingChildRoutes.has("settings/email-recipients")) {
            additionalChildren.push(buildEmailRecipientsItem(item));
          }
          if (additionalChildren.length > 0) {
            item = {
              ...item,
              children: [...(item.children || []), ...additionalChildren],
            };
          }
        }

        if (
          userType.value === "user" &&
          canManageNotifications.value &&
          isUsersManagementItem(item) &&
          !hasNotificationManagementItem
        ) {
          injectedItems.push(buildNotificationManagementItem(item));
        }

        if (shouldInjectLeadItem && isChatNavItem(item)) {
          injectedItems.push(buildLeadsManagementItem(section));
        }

        if (shouldInjectOnboardingItem && isOverviewNavItem(item)) {
          injectedItems.push(buildOnboardingManagementItem(section));
        }

        let finalItem = item;
        if (isInboxNavItem(item)) {
          finalItem = {
            ...item,
            icon: inboxIcon,
          };
        }

        const shouldHideUsersManagementItem =
          isUsersManagementItem(item) && !canAccessRoute("/users", authStore);

        return shouldHideUsersManagementItem
          ? injectedItems
          : [finalItem, ...injectedItems];
      });

      if (
        shouldInjectLeadItem &&
        !activeItems.some((item) => isChatNavItem(item))
      ) {
        results.push(buildLeadsManagementItem(section));
      }

      if (shouldInjectInboxItemIntoSection(section)) {
        const inboxItem = buildInboxManagementItem(section);
        const overviewIndex = results.findIndex((item) => {
          const normalizedRoute = String(item?.route || "")
            .trim()
            .toLowerCase()
            .replace(/^\/+/g, "");

          return normalizedRoute === "overview";
        });

        if (overviewIndex >= 0) {
          results.splice(overviewIndex + 1, 0, inboxItem);
        } else {
          results.unshift(inboxItem);
        }
      }

      if (
        shouldInjectEmailTemplatesItemIntoSection(section) &&
        !hasEmailTemplatesItem
      ) {
        const emailTemplatesItem = buildEmailTemplatesItem(section);
        const inboxIndex = results.findIndex((item) => isInboxNavItem(item));

        if (inboxIndex >= 0) {
          results.splice(inboxIndex + 1, 0, emailTemplatesItem);
        } else {
          const overviewIndex = results.findIndex((item) => {
            const normalizedRoute = String(item?.route || "")
              .trim()
              .toLowerCase()
              .replace(/^\/+/g, "");

            return normalizedRoute === "overview";
          });

          if (overviewIndex >= 0) {
            results.splice(overviewIndex + 1, 0, emailTemplatesItem);
          } else {
            results.unshift(emailTemplatesItem);
          }
        }
      }

      if (
        shouldInjectOnboardingItem &&
        !activeItems.some((item) => isOverviewNavItem(item))
      ) {
        results.push(buildOnboardingManagementItem(section));
      }

      const hasReportsV2Already = results.some(
        (item) =>
          item?.id === "reports-v2-navigation-group" ||
          normalizeRoutePath(item?.route) === "reports-v2"
      );

      if (!hasReportsV2Already && canAccessReportsV2.value) {
        const reportsIndex = results.findIndex(
          (item) => normalizeRoutePath(item?.route) === "reports"
        );

        if (reportsIndex >= 0) {
          results.splice(reportsIndex + 1, 0, buildReportsV2Item(section));
        }
      }

      const accessFilteredResults = canAccessReportsV2.value
        ? results
        : results.filter(
            (item) =>
              item?.id !== "reports-v2-navigation-group" &&
              normalizeRoutePath(item?.route) !== "reports-v2"
          );

      return groupSettingsItems(
        groupUsersAccessItems(
          groupManagementItems(groupOnboardingItems(accessFilteredResults))
        )
      );
    };

    const orderedSections = computed(() => {
      const sections = [...sidenavSections.value].sort(
        (a, b) => a.order - b.order
      );
      return sections.map((section) => ({
        ...section,
        displayItems: getSectionItems(section),
      }));
    });

    watch(
      () => router.currentRoute.value.path,
      (path) => {
        if (
          [
            "/settings/system-apps",
            "/settings/system",
            "/settings/email-recipients",
            "/settings/notifications",
            "/settings/holidays"
          ].includes(path)
        ) {
          const allDisplayItems = orderedSections.value.flatMap(
            (s) => s.displayItems || []
          );
          const settingsParent = allDisplayItems.find(
            (item) =>
              item?.id === "settings-navigation-group" ||
              String(item?.label || "").trim().toLowerCase() === "settings" ||
              (Array.isArray(item?.children) &&
                item.children.some((c) =>
                  [
                    "settings/system-apps",
                    "settings/system",
                    "settings/email-recipients",
                    "settings/notifications",
                    "/settings/holidays"
                  ].includes(normalizeRoutePath(c?.route))
                ))
          );
          const targetId = String(
            settingsParent?.id || "settings-navigation-group"
          );
          if (!expandedNavGroups.value.includes(targetId)) {
            expandedNavGroups.value = [...expandedNavGroups.value, targetId];
          }
        }

        if (
          path === "/clients" ||
          path === "/renewals" ||
          path === "/onboarding/microapp"
        ) {
          const clientsGroupId = "clients-navigation-group";
          if (!expandedNavGroups.value.includes(clientsGroupId)) {
            expandedNavGroups.value = [
              ...expandedNavGroups.value,
              clientsGroupId,
            ];
          }
        }

        if (
          path === "/onboarding" ||
          (path.startsWith("/onboarding/") && path !== "/onboarding/microapp")
        ) {
          const onboardingGroupId = "onboarding-navigation-group";
          if (!expandedNavGroups.value.includes(onboardingGroupId)) {
            expandedNavGroups.value = [
              ...expandedNavGroups.value,
              onboardingGroupId,
            ];
          }
        }

        if (path === "/reports-v2" || path.startsWith("/reports-v2/")) {
          const reportsV2GroupId = "reports-v2-navigation-group";
          if (!expandedNavGroups.value.includes(reportsV2GroupId)) {
            expandedNavGroups.value = [
              ...expandedNavGroups.value,
              reportsV2GroupId,
            ];
          }
        }
      },
      { immediate: true }
    );

    const handleClientLoggedIn = async () => {
      await refreshChatUnreadBadge();
    };

    onMounted(async () => {
      await refreshChatUnreadBadge();
      window.addEventListener("client-logged-in", handleClientLoggedIn);
      eventBus.on("client-logged-in", handleClientLoggedIn);
    });

    onBeforeUnmount(() => {
      clearChatUnreadSubscriptions();
      window.removeEventListener("client-logged-in", handleClientLoggedIn);
      eventBus.off("client-logged-in", handleClientLoggedIn);
    });

    return {
      userType,
      isSuperAdmin,
      canAccessReportsV2,
      canCreateClientApplications,
      sidenavQuery,
      isSidenavLoading,
      sidenavItems,
      sidenavSections,
      orderedSections,
      navigateTo,
      isActive,
      toggleCollapse,
      onToggleClick,
      onBrandNavigate,
      formatCategoryTitle: (cat) => cat,
      isImageIcon,
      resolveIconUrl,
      getSectionItems,
      hasNavChildren,
      isNavGroupOpen,
      handleNavItemClick,
      isChatNavItem,
      getChatBadgeCount,
      getNotificationBadgeCount,
      badgeCountText,
      chatBadgeClass,
      dmsLogo,
      sidebarLogo,
    };
  },
};
</script>

<style scoped>
.side-nav {
  scrollbar-width: none;
  overflow-x: visible;
}

.scrollbar-hidden {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.scrollbar-hidden::-webkit-scrollbar {
  display: none;
}

/* Desktop: fixed full-height rail, flush left; width animates with main-column margin */
.side-navigation-desktop {
  position: fixed;
  left: 0;
  top: 0;
  z-index: 40;
  height: 100vh;
  height: 100dvh;
  width: 16rem;
  max-width: 16rem;
  overflow: hidden;
  border-right: 1px solid rgb(229 231 235);
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.side-navigation-desktop.collapsed {
  width: 4rem;
  max-width: 4rem;
  overflow: visible !important;
}

.side-navigation-desktop.collapsed .nav-container,
.side-navigation-desktop.collapsed .side-nav,
.side-navigation-desktop.collapsed .scrollbar-hidden {
  overflow: visible !important;
}

.side-navigation-desktop.collapsed h2,
.side-navigation-desktop.collapsed hr,
.side-navigation-desktop.collapsed .nav-section-title,
.side-navigation-desktop.collapsed .nav-divider {
  display: none !important;
}

.side-navigation-desktop.collapsed .nav-item {
  justify-content: center !important;
  padding-left: 0.5rem !important;
  padding-right: 0.5rem !important;
  gap: 0 !important;
}

.side-navigation-desktop.collapsed .nav-item > span:not(.nav-item-icon-wrap) {
  display: none !important;
}

.side-navigation-desktop.collapsed .nav-chevron,
.side-navigation-desktop.collapsed .nav-child-list {
  display: none !important;
}

.side-navigation-desktop.collapsed .sticky-bottom-section .nav-item {
  justify-content: center !important;
  padding-left: 0.5rem !important;
  padding-right: 0.5rem !important;
  gap: 0 !important;
}

.side-navigation-desktop.collapsed .sticky-bottom-section .nav-item > span:not(.nav-item-icon-wrap) {
  display: none !important;
}

.side-navigation-desktop:not(.collapsed) .nav-collapsed-flyout,
.side-navigation-desktop:not(.collapsed) .nav-collapsed-tooltip,
.side-navigation-mobile .nav-collapsed-flyout,
.side-navigation-mobile .nav-collapsed-tooltip {
  display: none !important;
}

.nav-item img {
  transition: transform 0.2s ease;
}

.nav-item:hover {
  color: #fff;
}

.nav-item-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
}

.sticky-bottom-section {
  width: 100%;
}

/* Mobile / tablet drawer: full viewport height, above overlay */
.side-navigation-mobile {
  position: fixed;
  left: 0;
  top: 0;
  z-index: 60;
  width: min(16rem, 85vw);
  height: 100vh;
  height: 100dvh;
  max-width: 85vw;
  border-right: 1px solid rgb(229 231 235);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.08);
}

.side-navigation-mobile-closed {
  transform: translate3d(-100%, 0, 0);
  pointer-events: none;
}

.side-navigation-mobile-open {
  transform: translate3d(0, 0, 0);
  pointer-events: auto;
}
</style>
